import CircularProgress from '@mui/material/CircularProgress'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectChangeEvent, SelectProps } from '@mui/material/Select'
import React, { useCallback, useEffect, useState } from 'react'
import { authRequest } from '../util/api/auth-api'
import checkToken from '../util/auth/checkToken'
import handleError from '../util/handleError'
import useStore from '../util/store/auth'

export type DirectorySelectorProps = Omit<SelectProps<string>, 'onChange' | 'value'>

const DirectorySelect = ({ ...props }: DirectorySelectorProps, ref: React.Ref<HTMLDivElement>) => {
  const [loading, setLoading] = useState(false)
  const { directories, setDirectories, directory, setDirectory, user } = useStore() // Use this to store the listed directories

  const populateOptions = useCallback(
    async (search?: string) => {
      try {
        if (!user) {
          setDirectories([])
          setDirectory(null)
          return
        }

        await checkToken()

        setLoading(true)

        const data = await authRequest.user.directories.list({
          token: '',
          search: search
            ? {
                and: [{ name: search, $fuzzy: true }],
              }
            : undefined,
        })

        setDirectories(data.results)

        if (data.results.length === 1) {
          setDirectory(data.results[0])
        }
      } catch (err) {
        handleError(err)
      } finally {
        setLoading(false)
      }
    },
    [setDirectories, setDirectory, user]
  )

  useEffect(() => {
    const timeout = setTimeout(() => {
      void populateOptions()
    }, 10)

    return () => {
      clearTimeout(timeout)
    }
  }, [populateOptions])

  // Find the directory by ID for the value
  const directoryId = directory ? directory._id : ''
  const hasCurrentDirectoryInOptions =
    !!directory && directories.some((dir) => dir._id === directory._id)
  // Only admins can temporarily view a directory that is outside their normal list.
  const shouldIncludeCurrentDirectory =
    !!user?.admin && !!directory && !hasCurrentDirectoryInOptions
  // Include the selected directory so the Select can render its label.
  const directoryOptions = shouldIncludeCurrentDirectory ? [directory, ...directories] : directories

  // Handle directory selection
  const handleChange = (event: SelectChangeEvent<string>) => {
    const selectedId = event.target.value
    const selectedDirectory = directoryOptions.find((dir) => dir._id === selectedId) ?? null
    setDirectory(selectedDirectory)
  }

  return (
    <Select
      {...props}
      ref={ref}
      value={directoryId}
      onChange={handleChange}
      id="directory-select"
      data-qa-id="nav-organization-select"
      size="small"
      displayEmpty
      renderValue={(selected) => {
        if (selected === '') {
          return <em>Select an organization</em>
        }
        const selectedDirectory = directoryOptions.find((dir) => dir._id === selected)
        return selectedDirectory ? selectedDirectory.name : ''
      }}
      endAdornment={loading ? <CircularProgress color="inherit" size={20} /> : null}
      disabled={!user}
    >
      {directoryOptions.map((dir) => (
        <MenuItem key={dir._id} value={dir._id}>
          {dir.name}
        </MenuItem>
      ))}
    </Select>
  )
}

export default React.forwardRef(DirectorySelect)
